import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppProvider';
import { Target, AlertCircle, RefreshCw, Download } from 'lucide-react';
import API_CONFIG from '../../config/api';

const BASE_URL = API_CONFIG.BASE_URL;

function SingleStatComponent({ value, description }) {
  return (
	<div 
	  className="p-6 border shadow-sm transition-all duration-200 hover:shadow-md"
	  style={{ 
		backgroundColor: 'var(--color-surface)',
		borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
	  }}
	>
	  <div className="flex flex-col items-center justify-center text-center">
		<div 
		  className="text-4xl font-bold mb-2"
		  style={{ color: 'var(--color-primary)' }}
		>
		  {value}
		</div>
		<div 
		  className="text-sm font-medium"
		  style={{ color: 'var(--color-muted)' }}
		>
		  {description}
		</div>
	  </div>
	</div>
  );
}

function QuotaTable({ rows }) {
	const handleDownloadCSV = () => {
	  const headers = ['Quota Name', 'Target', 'Current', 'Conditions', 'Type', 'Progress (%)'];
	  const csvRows = [headers.join(',')];
	  
	  rows.forEach(row => {
		const progress = Math.min((row.current_count / row.target_count) * 100, 100);
		const csvRow = [
		  `"${row.label}"`,
		  row.target_count,
		  row.current_count,
		  row.conditions_count,
		  row.enforcement_type,
		  Math.round(progress)
		];
		csvRows.push(csvRow.join(','));
	  });
	  
	  const csvContent = csvRows.join('\n');
	  const blob = new Blob([csvContent], { type: 'text/csv' });
	  const url = window.URL.createObjectURL(blob);
	  const a = document.createElement('a');
	  a.href = url;
	  a.download = `quota-tracking-${new Date().toISOString().split('T')[0]}.csv`;
	  document.body.appendChild(a);
	  a.click();
	  document.body.removeChild(a);
	  window.URL.revokeObjectURL(url);
	};

	return (
	  <div 
		className="border shadow-sm overflow-hidden relative"
		style={{ 
		  backgroundColor: 'var(--color-surface)',
		  borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
		}}
	  >
		<button
		  onClick={handleDownloadCSV}
		  className="absolute pt-3 right-1 p-2 transition-all duration-200 hover:shadow-md z-10"
		  title="Download as CSV"
		>
		  <Download className="h-4 w-4" style={{ color: 'var(--color-primary)' }} />
		</button>
		
		<div className="overflow-x-auto">
		  <table className="w-full">
			<thead style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary) 10%, var(--color-surface))' }}>
			  <tr>
				<th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text)', width: '20%' }}>
				  Quota Name
				</th>
				<th className="px-6 py-3 text-center text-sm font-semibold" style={{ color: 'var(--color-text)', width: '10%' }}>
				  Target
				</th>
				<th className="px-6 py-3 text-center text-sm font-semibold" style={{ color: 'var(--color-text)', width: '10%' }}>
				  Current
				</th>
				<th className="px-6 py-3 text-center text-sm font-semibold" style={{ color: 'var(--color-text)', width: '10%' }}>
				  Conditions
				</th>
				<th className="px-6 py-3 text-center text-sm font-semibold" style={{ color: 'var(--color-text)', width: '10%' }}>
				  Type
				</th>
				<th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text)', width: '40%' }}>
				  Progress
				</th>
			  </tr>
			</thead>
			<tbody>
			  {rows.map((row, index) => {
				const progress = Math.min((row.current_count / row.target_count) * 100, 100);
				const isComplete = row.current_count >= row.target_count;
				
				return (
				  <tr 
					key={row.quota_id}
					style={{ 
					  borderTop: index > 0 ? `1px solid color-mix(in srgb, var(--color-muted) 30%, transparent)` : 'none'
					}}
				  >
					<td className="px-6 py-4">
					  <span className="font-bold" style={{ color: 'var(--color-text)' }}>
						{row.label}
					  </span>
					</td>
					<td className="px-6 py-4 text-center" style={{ color: 'var(--color-text)' }}>
					  {row.target_count}
					</td>
					<td className="px-6 py-4 text-center" style={{ color: 'var(--color-text)' }}>
					  {row.current_count}
					</td>
					<td className="px-6 py-4 text-center" style={{ color: 'var(--color-text)' }}>
					  {row.conditions_count}
					</td>
					<td className="px-6 py-4 text-center">
					  <span 
						className="inline-block px-3 py-1 text-xs font-semibold"
						style={{ 
						  backgroundColor: row.enforcement_type === 'HARD' 
							? 'color-mix(in srgb, var(--color-primary) 20%, transparent)'
							: 'color-mix(in srgb, var(--color-muted) 20%, transparent)',
						  color: 'var(--color-text)'
						}}
					  >
						{row.enforcement_type}
					  </span>
					</td>
					<td className="px-6 py-4">
					  <div className="flex items-center gap-3">
						<div className="flex-1 h-2 relative" style={{ backgroundColor: 'var(--color-bg)' }}>
						  <div 
							className="absolute top-0 left-0 h-full transition-all duration-300"
							style={{ 
							  width: `${progress}%`,
							  backgroundColor: isComplete ? 'var(--color-primary)' : 'color-mix(in srgb, var(--color-primary) 60%, transparent)'
							}}
						  />
						</div>
						<span className="text-sm font-medium min-w-[3rem] text-right" style={{ color: 'var(--color-text)' }}>
						  {Math.round(progress)}%
						</span>
					  </div>
					</td>
				  </tr>
				);
			  })}
			</tbody>
		  </table>
		</div>
	  </div>
	);
}

export default function QuotasTrackingFeature({ featureName }) {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [quotaData, setQuotaData] = useState(null);
	const { tenantInfos, t } = useApp();
  
  	const fetchFeatureData = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const tenant_id = tenantInfos.interfaceId;
			const access_token = tenantInfos.accessToken;
			const api_url = `${BASE_URL}/cui_portal/${tenant_id}/${access_token}/features/get_data/${featureName}`;
			
			const result = await fetch(api_url);
			
			if (result.status === 404) {
				setError({ statusCode: 404, detail: "Feature data not found" });
				return;
			}
			if (result.status === 401) {
				setError({ statusCode: 401, detail: "You are not authorized to access this feature.\nPlease contact your administrator" });
				return;
			}
			if (!result.ok) {
				setError({ statusCode: 500, detail: "Unexpected error happened" });
				return;
			}
			
			const response = await result.json();
			
			if (response.success && response.data) {
				setQuotaData(response.data);
				setError(null);
			} else if (response.error) {
				setError({ statusCode: response.error.status_code, detail: response.error.detail || response.error.message });
			} else {
				setError({ statusCode: 422, detail: "Unexpected data structure" });
			}
		} catch (err) {
			console.error("Error fetching feature data:", err);
			setError({ statusCode: 500, detail: "Failed to fetch feature data" });
		} finally {
			setIsLoading(false);
		}
	}, [featureName, tenantInfos]);
  
	useEffect(() => {
		fetchFeatureData();
	}, [fetchFeatureData]);
  
	const handleRefreshButton = () => {
		fetchFeatureData();
	};

  // Loading state
  if (isLoading) {
	return (
	  <div className="max-w-7xl mx-auto px-6 py-12">
		<div className="flex items-center justify-center min-h-[400px]">
		  <div className="text-center">
			<div 
			  className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
			  style={{ borderColor: 'var(--color-primary)' }}
			/>
			<p style={{ color: 'var(--color-muted)' }}>Loading quota data...</p>
		  </div>
		</div>
	  </div>
	);
  }

  // Error state
  if (error) {
	return (
	  <div className="max-w-7xl mx-auto px-6 py-12">
		<div className="flex items-center justify-center min-h-[400px]">
		  <div 
			className="p-6 border"
			style={{ 
			  backgroundColor: 'color-mix(in srgb, red 10%, var(--color-surface))',
			  borderColor: 'color-mix(in srgb, red 30%, transparent)'
			}}
		  >
			<div className="flex items-start gap-3">
			  <AlertCircle className="h-6 w-6 flex-shrink-0" style={{ color: 'red' }} />
			  <div>
				<h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
				  Error {error.statusCode}
				</h3>
				<p style={{ color: 'var(--color-text)', whiteSpace: 'pre-line' }}>
				  {error.detail}
				</p>
			  </div>
			</div>
		  </div>
		</div>
	  </div>
	);
  }

  return (
	<div className="mx-auto px-6 py-8">
	  {/* Header */}
	  <div className="flex items-center justify-between mb-6">
		<div className="flex items-end gap-2">
		  <Target className="h-8 w-8 mb-1" style={{ color: 'var(--color-primary)' }} />
		  <h1 className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
			{t("quotasTitle")}
		  </h1>
		</div>
		<button
		  onClick={handleRefreshButton}
		  className="px-4 py-2 border transition-all duration-200 hover:shadow-md"
		  style={{
			backgroundColor: 'var(--color-surface)',
			borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)',
			color: 'var(--color-text)'
		  }}
		  onMouseEnter={(e) => {
			e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 5%, var(--color-surface))';
		  }}
		  onMouseLeave={(e) => {
			e.currentTarget.style.backgroundColor = 'var(--color-surface)';
		  }}
		>
		  <div className="flex items-center gap-2">
			<RefreshCw className="h-4 w-4" />
			<span className="text-sm font-medium">{t("refresh")}</span>
		  </div>
		</button>
	  </div>

	  {/* Stats Row */}
	  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
		<SingleStatComponent 
		  value={quotaData?.total_target || 0} 
		  description={t("quotasSingleStatsTarget")}
		/>
		<SingleStatComponent 
		  value={quotaData?.total_current || 0} 
		  description={t("quotasSingleStatsCurrent")}
		/>
		<SingleStatComponent 
		  value={quotaData?.overall_fulfilled_count || 0} 
		  description={t("quotasSingleStatsFilled")}
		/>
		<SingleStatComponent 
		  value={`${Math.round(quotaData?.overall_progress_percent || 0)}%`}
		  description={t("quotasSingleStatsOverallProgress")}
		/>
		<SingleStatComponent 
		  value={`${Math.round(quotaData?.fulfilled_quota_percent || 0)}%`}
		  description={t("quotasSingleStatsFullfillmentRate")}
		/>
	  </div>

	  {/* Table */}
	  {quotaData?.rows && quotaData.rows.length > 0 ? (
		<QuotaTable rows={quotaData.rows} />
	  ) : (
		<div 
		  className="p-8 border text-center"
		  style={{ 
			backgroundColor: 'var(--color-surface)',
			borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
		  }}
		>
		  <p style={{ color: 'var(--color-muted)' }}>No quota data available</p>
		</div>
	  )}
	</div>
  );
}